import { Link } from "react-router-dom";
import type { EventItem } from "@/mocks/events";
import StatusBadge from "@/components/feature/StatusBadge";
import { formatShortDate, getCategoryName, getCategoryIcon } from "@/lib/eventUtils";

interface EventCardProps {
  event: EventItem;
  variant?: "default" | "compact";
}

export default function EventCard({ event, variant = "default" }: EventCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/events/${event.id}`}
        className="group flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="shrink-0 w-20 h-20 rounded-md overflow-hidden bg-background-200">
          <img
            src={event.cover}
            alt={`${event.title}事件封面插图`}
            title={`${event.title} - 事件进展与真相梳理`}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="min-w-0 flex flex-col">
          <div className="flex items-center gap-2 text-[11px] text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className={getCategoryIcon(event.category)} />
              {getCategoryName(event.category)}
            </span>
            <span>·</span>
            <span>{formatShortDate(event.updatedAt)}</span>
          </div>
          <h3 className="mt-1.5 font-heading text-sm font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
            {event.title}
          </h3>
        </div>
      </Link>
    );
  }

  return (
    <article className="group h-full flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors">
      <Link to={`/events/${event.id}`} className="block cursor-pointer">
        <div className="w-full h-44 md:h-48 overflow-hidden bg-background-200">
          <img
            src={event.cover}
            alt={`${event.title}事件封面插图`}
            title={`${event.title} - 事件进展与真相梳理`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </div>
      </Link>
      <div className="flex-1 flex flex-col p-5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-[11px] text-foreground-600 bg-secondary-100 rounded-full px-2.5 py-1">
            <i className={getCategoryIcon(event.category)} />
            {getCategoryName(event.category)}
          </span>
          <StatusBadge status={event.status} />
        </div>
        <h3 className="mt-3 font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug">
          <Link to={`/events/${event.id}`} className="hover:text-primary-700 transition-colors cursor-pointer">
            {event.title}
          </Link>
        </h3>
        <p className="mt-2.5 text-sm leading-relaxed text-foreground-600 line-clamp-3">{event.summary}</p>
        <div className="mt-4 pt-4 border-t border-background-100 flex items-center justify-between text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line" />
            {formatShortDate(event.updatedAt)} 更新
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-fire-line text-primary-500" />
            热度 {event.heat}
          </span>
        </div>
      </div>
    </article>
  );
}